(function () {
    class StoreSettingsComponent {
        constructor(
            $scope,
            $uibModal,
            APIService,
            PermissionService,
            ErrorService
        ) {
            this.$scope = $scope;
            this.$uibModal = $uibModal;
            this.apiService = APIService;
            this.permissionService = PermissionService;
            this.errorService = ErrorService;
            this.$scope.loading = false;
            this.store = null;
            this.province = "";
            this.name = "";
            this.address = "";
            this.orderCompleteNotificationFeature = null;
            this.storeIsLive = false;
            this.$scope.error = false;
            this.kerbsideCollectMobileNumber = "";
            this.waiterMobileNumber = "";
            this.showEditWaiterNumberButton = false;
            this.showEditWaiterNumberForm = false;
            this.initialise();
        }

        async handleError(error) {
            this.errorService.showError(
                "An error occurred while fulfilling your action",
                error
            );
            await this.fetchStore();
        }

        updateView() {
            if (!this.store) {
                return;
            }
            this.name = this.store.Name;
            this.address = this.store.Address;
            this.storeIsLive = this.store.State === Dashboard.StoreState.Live
            this.$scope.error = !this.storeIsLive
                && this.store.State !== Dashboard.StoreState.Unavailable;
            this.kerbsideCollectMobileNumber = this.store.KerbsideCollectMobileNumber
            this.waiterMobileNumber = this.kerbsideCollectMobileNumber
            this.showEditWaiterNumberButton = !!this.kerbsideCollectMobileNumber
            this.showEditWaiterNumberForm = !this.waiterMobileNumber;
        }

        showOrderReadyNotificationsToggle() {
            return !!this.orderReadyNotificationsFeature()
                && this.store.UseOrderUpChannel;
        }

        orderReadyNotificationsFeature() {
            if (!this.orderCompleteNotificationFeature) {
                console.warn("SendCustomerNotificationsWhenOrdersAreCompleted feature does not exist");
                return null;
            }
            return this.orderCompleteNotificationFeature;
        }

        orderReadyNotificationsEnabled() {
            if (!this.store) {
                return false;
            }
            const feature = this.orderReadyNotificationsFeature();
            return !!feature?.Enabled;
        }

        async fetchStoreProvince() {
            if (!this.store) {
                return;
            }
            if (!this.store.ProvinceId) {
                this.errorService.showError(
                    "This store has been improperly configured: it has no associated province.",
                    null
                );
                return;
            }
            try {
                const result = await this.apiService.Provinces.getProvinceById(this.store.ProvinceId);
                this.province = result.data.Name;
            } catch (e) {
                this.errorService.showError(
                    "An error occurred while fetching store information",
                    e
                );
            }
        }

        async fetchStore() {
            this.$scope.loading = true;
            const storeId = this.permissionService.userContext.StoreId;
            try {
                const result = await this.apiService.Stores.getStoreById(storeId);
                this.store = result.data;
                this.updateView();
                await this.fetchStoreProvince();
            } catch (e) {
                this.errorService.showError(
                    "An error occurred while fetching store information",
                    e
                );
                this.$scope.error = true
            } finally {
                this.$scope.loading = false;
            }
            this.$scope.$applyAsync();
        }

        checkUserHasPermission() {
            const userCanToggleOrders = this.permissionService
                .doesUserHavePermissionForContext(
                    Dashboard.Models.Lookups.Permissions.StoreSettings,
                    this.permissionService.userContext
                );
            if (!userCanToggleOrders) {
                this.permissionService.navigateToAccessDenied();
            }
        }

        async enableOnlineOrders() {
            try {
                await this.apiService.Stores.toggleAllowOnlineOrders(
                    this.store.Id,
                    true
                );
                await this.fetchStore();
            } catch (e) {
                await this.handleError(e);
            }
        }

        async performOnlineOrdersToggle() {
            try {
                await this.apiService.Stores.toggleAllowOnlineOrders(
                    this.store.Id,
                    false
                );
                await this.fetchStore();
            } catch (e) {
                await this.handleError(e);
            }
        }

        async toggleOnlineOrders() {
            if (!this.store) {
                return;
            }
            try {
                if (this.store.State === Dashboard.StoreState.Live) {
                    await this.performOnlineOrdersToggle();
                } else if (this.store.State === Dashboard.StoreState.Unavailable) {
                    await this.enableOnlineOrders();
                }
            } catch (e) {
                await this.handleError(e);
            }
        }

        async performOrderCompleteNotificationsToggle() {
            try {
                const enabled = !this.orderReadyNotificationsEnabled();
                await this.apiService.Stores.toggleOrderCompleteNotifications(
                    this.store.Id,
                    enabled
                );
                await this.fetchStore();
                const toggle = window.Dashboard.helpers.features
                    .fetchSendCustomerNotificationsWhenOrdersAreCompletedFeatureFor(this.store);
                if (toggle?.Enabled === enabled) {
                    this.setupOrderCompleteNotificationsFeature();
                    return;
                }
                this.scheduleOrderCompleteNotificationToggle(enabled);
            } catch (e) {
                await this.handleError(e);
            }
        }

        scheduleOrderCompleteNotificationToggle(enabled) {
            console.warn("Feature toggle value does not match expected, checking again in 5 seconds");
            this.orderCompleteNotificationFeature.Enabled = enabled;
            window.setTimeout(
                this.featureResultMismatchHandler(enabled),
                5000
            );
        }

        featureResultMismatchHandler(enabled) {
            return async () => {
                await this.fetchStore();
                const toggle = window.Dashboard.helpers.features
                    .fetchSendCustomerNotificationsWhenOrdersAreCompletedFeatureFor(this.store);
                if (toggle?.Enabled !== enabled) {
                    this.setupOrderCompleteNotificationsFeature();
                    this.errorService.showError(
                        `Notifications could not be ${enabled ? "enabled" : "disabled"}. Please try again later.`
                    );
                }
            };
        }

        async toggleOrderCompleteNotifications() {
            if (!this.store) {
                return;
            }
            try {
                await this.performOrderCompleteNotificationsToggle();
            } catch (e) {
                await this.handleError(e);
            }
        }

        editWaiterMobileNumber() {
            this.showEditWaiterNumberButton = false;
            this.showEditWaiterNumberForm = true;
        }

        cancelEditingWaiterMobileNumber() {
            this.showEditWaiterNumberButton = true;
            this.showEditWaiterNumberForm = false;
            this.updateView();
        }

        async updateWaiterMobileNumber() {
            if (!this.store) {
                return;
            }
            try {
                await this.apiService.Stores
                    .updateKerbsideCollectMobileNumber(
                        this.store.Id,
                        this.waiterMobileNumber || ""
                    );
                await this.fetchStore();
            } catch (e) {
                this.errorService.showError(
                    "An error occurred while attempting to update number",
                    e
                );
            }
        }

        async initialise() {
            this.address = "";
            this.province = "";
            this.canToggle = false;
            this.actionLabel = "..."; // This won't be necessary when UI design is complete to spec
            this.loading = false;
            this.checkUserHasPermission();
            await this.fetchStore();
            this.setupOrderCompleteNotificationsFeature();
        }

        setupOrderCompleteNotificationsFeature() {
            if (!this.store) {
                return;
            }
            this.orderCompleteNotificationFeature = window.Dashboard.helpers.features
                .fetchSendCustomerNotificationsWhenOrdersAreCompletedFeatureFor(this.store);
        }

        onToggleOnlineOrders() {
            const modalInstance = this.$uibModal.open({
                animation: true,
                templateUrl: "components/store/settings/toggleorders/toggleorders.0e481745.html",
                controller: "ToggleOrdersModalController",
                size: "lg",
                backdrop: "static",
                resolve: {
                    name: () => {
                        return this.store.Name
                    },
                    state: () => {
                        return this.store.State
                    }
                }
            });

            modalInstance.result.then(async (result) => {
                if (result === true) {
                    await this.toggleOnlineOrders();
                }
            })
        }

        onToggleOrderCompleteNotifications() {
            const modalInstance = this.$uibModal.open({
                animation: true,
                templateUrl: "components/store/settings/ordercompletenotifications/toggle-notifications.35a2e690.html",
                controller: "ToggleNotificationsModalController",
                size: "lg",
                backdrop: "static",
                resolve: {
                    name: () => {
                        return this.store.Name
                    },
                    enabled: () => {
                        return this.orderReadyNotificationsEnabled()
                    }
                }
            });

            modalInstance.result.then(async (result) => {
                if (result === true) {
                    await this.toggleOrderCompleteNotifications();
                }
            })
        }
    }

    angular
        .module("dashboard")
        .component("storeSettings", {
            templateUrl: "components/store/settings/settings.20105d0d.html",
            bindings: {},
            controller: [
                "$scope",
                "$uibModal",
                "APIService",
                "PermissionService",
                "ErrorService",
                StoreSettingsComponent
            ]
        });
})();
